import {Component, Input, OnInit, Output, EventEmitter} from "@angular/core";
import {Message, MessageCollection, MessageMetadata, MessageMetadataProfile} from "../../shared/api/messages/api.models";
import {MessagesSettings} from "./messages.settings";
import {Attachment} from "../../shared/api/attachments/api.models";
import {ActivatedRoute} from "@angular/router";
import {Api} from "../../shared/api.service";
import {GroupedLanguages} from "../../shared/languages.resolver";
import * as moment from 'moment';
import {MessageOptions} from "./components/options.model";
import {ButtonOption} from "../../shared/button-options.component";
import {ModalService} from "../../shared/modal.service";
import {Utils} from "../../shared/utils.service";


@Component({
    selector: 'messages',
    template: require('./messages.component.html'),
    styles: [require('./messages.component.scss')]
})

export class MessagesComponent implements OnInit{
    @Input()
    messageCollection: MessageCollection;

    @Input()
    settings: MessagesSettings;

    @Output()
    done = new EventEmitter();

    @Output()
    reload = new EventEmitter();

    public saving: boolean               = false;
    public saveSuccess: boolean          = false;
    public saveFailed: boolean           = false;
    public messageFlags: any[]           = [];
    public allValid: boolean             = false;
    public behaviours: string[]          = MessageCollection.behaviours;
    public tagOptions: string[]          = MessageCollection.tags;
    public genders                       = MessageCollection.genders;
    public languages: GroupedLanguages[] = [];
    public variables;

    protected currentScreen;

    protected days: string[]                = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    protected relationships: ButtonOption[] = MessageOptions.relationShipOptions;
    protected weekDays: ButtonOption[]      = MessageOptions.daysOfWeekOptions;
    protected relationshipsHash: any        = {};
    protected weekDaysHash: any             = {};

    protected lookingFors: ButtonOption[]   = MessageOptions.lookingFor;
    protected photos: ButtonOption[]        = MessageOptions.photos;
    protected times: string[]               = [];
    protected nearby: ButtonOption[]        = MessageOptions.nearby;
    protected followUpAllowedTags: string[] = [MessageCollection.TAG_DEFAULT, MessageCollection.TAG_LEAD_2_SALE, MessageCollection.TAG_INSECURE];
    protected metadata: any;

    protected ageRangeConfig: any = {
        behaviour: 'drag',
        connect: true,
        range: {
            min: 18,
            max: 99
        },
        step: 1,
        tooltips: true,
        pips: {
            mode: 'values',
            values: [18, 25, 35, 45, 55, 99],
            density: 5
        }
    };

    protected timeRangeOverTwoDays: boolean = false;
    protected timeRangeValid: boolean       = true;
    protected showMessagePreview: boolean   = false;

    constructor(private route: ActivatedRoute, private api: Api, private modal: ModalService, private utils: Utils) {}

    ngOnInit() {
        this.currentScreen = this.settings.startScreen;
        this.languages = this.route.snapshot.data['languages'] as GroupedLanguages[];
        this.utils.hash(this.relationshipsHash, this.relationships);
        this.utils.hash(this.weekDaysHash, this.weekDays);

        if (!this.messageCollection.metadata) {
            this.messageCollection.metadata = new MessageMetadata();
        }

        // Older messages which does not have those objects
        if (!this.messageCollection.metadata.receiver) {
            this.messageCollection.metadata.receiver = new MessageMetadataProfile();
        }

        if (!this.messageCollection.metadata.sender) {
            this.messageCollection.metadata.sender = new MessageMetadataProfile();
        }

        // @DEPRECATED tags will be removed
        if (!this.messageCollection.metadata.tags) {
            this.messageCollection.metadata.tags = [MessageCollection.TAG_DEFAULT];
        }

        for (let i = 0; i <= 23; i++) {
            this.times.push((i < 10 ? '0' : '') + i + ':00');
            this.times.push((i < 10 ? '0' : '') + i + ':30');
        }
        this.variables = MessageCollection.variables;
        this.resetState();
    }

    ngOnChanges() {
        this.resetState();
    }

    toggleMessage(index) {
        this.messageFlags[index].expanded = !this.messageFlags[index].expanded;
    }

    addMessage() {
        let message = new Message();
        message.subject = this.messageCollection.messages[this.messageCollection.messages.length - 1].subject;

        // Collapse all the messages for faster editing
        this.messageFlags.forEach(value => value.expanded = false);
        this.messageCollection.messages.push(message);

        this.messageFlags.push({
            expanded : true,
            isValid  : false,
            settings : {
                showMessageType        : true,
                allowGenericAttachment : true
            }
        });

        this.determineMessageSettings();
    }

    delMessage(event: Event, index) {
        event.stopPropagation();

        this.messageCollection.messages.splice(index, 1);
        this.messageFlags.splice(index, 1);

        var allCollapsed = true;
        this.messageFlags.forEach((data) => {
            if (data.expanded === true) {
                allCollapsed = false;
            }
        });

        if (allCollapsed === true) {
            if (this.messageCollection.messages.length === 1) {
                this.toggleMessage(0);
            } else {
                this.toggleMessage(index -1);
            }
        }

        this.determineMessageSettings();

        return false;
    }

    messageUpdated(data: boolean, index) {
        this.messageFlags[index].isValid = data;
        this.determineMessageSettings();
    }

    changeGender(gender: string) {
        if (this.messageCollection.metadata.searchPreferences.gender === null) {
            this.messageCollection.metadata.searchPreferences.gender = gender;
            return;
        }

        this.messageCollection.metadata.searchPreferences.gender = null;
    }

    save() {
       this.saving      = true;

       this.api.messages().store(this.messageCollection).subscribe(
           () => {
                this.saving = false;
                this.toScreen('saveSuccess');
                this.settings.followUpAllowed = true;
            },
           () => {
                this.saving = false;
                this.toScreen('saveFailed');
            }
        );
    }

    approve() {
        this.messageCollection.state = MessageCollection.STATUS_APPROVED;
        this.save();
    }

    disapprove() {

        this.modal.confirm('Disapprove message', 'Are you sure you want to disapprove this message?').then(
            () => {
                this.messageCollection.state = MessageCollection.STATUS_DISAPPROVED;
                this.save();
            },
            () => {}
        );
    }

    continue() {
        this.toScreen(this.settings.startScreen);
        this.done.emit({});
    }

    addRange(collection) {
        collection.push([18, 99]);
        this.updateMetadata();
    }

    setRange(range: any[], index: number, min: number, max: number) {
        range[index] = [min, max];
        this.updateMetadata();
    }

    updateRange(event, collection, index) {
        this.setRange(collection, index, event[0], event[1]);
    }

    delRange(collection, index) {
        collection.splice(index, 1);
        this.updateMetadata();
    }

    updateMetadata() {
        if (this.metadata.timeRange.from && this.metadata.timeRange.till) {
            let timeA: string = this.metadata.timeRange.from;
            let timeB: string = this.metadata.timeRange.till;

            let momentA = moment().set({
                hour: Number(timeA.split(':')[0]),
                minute: Number(timeA.split(':')[1])
            });

            let momentB = moment().set({
                hour: Number(timeB.split(':')[0]),
                minute: Number(timeB.split(':')[1])
            });

            this.timeRangeOverTwoDays = (momentA.unix() > momentB.unix());
        }

        // Map everything on the API entity
        this.messageCollection.metadata.searchPreferences.gender    = this.metadata.receiver.lookingFor[0] || null;
        this.messageCollection.metadata.receiver.relationshipStatus = this.metadata.receiver.relationship;
        this.messageCollection.metadata.receiver.ageGroup           = this.metadata.receiver.ageRange;
        this.messageCollection.metadata.sender.relationshipStatus   = this.metadata.sender.relationship;
        this.messageCollection.metadata.nearby                      = this.metadata.nearby[0];
        this.messageCollection.metadata.receiver.hasPhoto           = (
            this.metadata.receiver.profilePhoto[0] === true ||this.metadata.receiver.profilePhoto[0] === false
        ) ? this.metadata.receiver.profilePhoto[0] : null;


        this.messageCollection.metadata.timerange = [];
        if (this.metadata.timeRange.from !== null) {
            this.messageCollection.metadata.timerange[0] = [
                this.metadata.timeRange.from,
                this.metadata.timeRange.till
            ];

            this.timeRangeValid = this.metadata.timeRange.till !== null;
        }

        // Day(s) of week
        this.messageCollection.metadata.daysOfWeek = this.metadata.daysOfWeek;
    }

    public resetState() {
        let list: any[];
        this.messageFlags = [];

        this.messageCollection.messages.forEach((message: Message, index: number) => {
            this.messageFlags[index] = {
                expanded : index === 0,
                isValid  : false,
                settings : {
                    showMessageType        : true,
                    allowGenericAttachment : true
                }
            }
        });

        this.metadata = {
            timeRange: {
                from: null,
                till: null
            },
            daysOfWeek: [],
            sender: {
                relationship: []
            },
            receiver : {
                relationship: [],
                profilePhoto: [],
                lookingFor:   [],
                ageRange:     []
            },
            nearby: []
        };

        if (this.messageCollection.metadata.sender) {
            this.metadata.sender.relationship = list = [];

            if (this.messageCollection.metadata.sender.relationshipStatus) {
                this.messageCollection.metadata.sender.relationshipStatus.forEach((item) => list.push(item));
            }
        }

        if (this.messageCollection.metadata.daysOfWeek) {
            this.metadata.daysOfWeek = list = [];

            if (this.messageCollection.metadata.daysOfWeek) {
                this.messageCollection.metadata.daysOfWeek.forEach((item) => list.push(item));
            }
        }

        if (this.messageCollection.metadata.receiver) {
            this.metadata.receiver.lookingFor          = [];
            this.metadata.receiver.profilePhoto        = [];
            this.metadata.receiver.relationship = list = [];
            this.metadata.receiver.ageRange            = [];

            if (this.messageCollection.metadata.receiver.relationshipStatus) {
                this.messageCollection.metadata.receiver.relationshipStatus.forEach((item) => list.push(item));
            }

            if (this.messageCollection.metadata.searchPreferences.gender) {
                this.metadata.receiver.lookingFor = [this.messageCollection.metadata.searchPreferences.gender];
            }

            if (this.messageCollection.metadata.receiver.hasPhoto !== null) {
                this.metadata.receiver.profilePhoto = [this.messageCollection.metadata.receiver.hasPhoto];
            }

            list = this.metadata.receiver.ageRange;
            if (this.messageCollection.metadata.receiver.ageGroup) {
                this.messageCollection.metadata.receiver.ageGroup.forEach((item) => list.push(item));
            }
        }

        if (this.messageCollection.metadata.timerange && this.messageCollection.metadata.timerange.length > 0) {
            // This moment we support only a single timerange
            this.metadata.timeRange.from = this.messageCollection.metadata.timerange[0][0];
            this.metadata.timeRange.till = this.messageCollection.metadata.timerange[0][1];
        }

        if (this.messageCollection.metadata.nearby !== null) {
            this.metadata.nearby = [this.messageCollection.metadata.nearby];
        }
    }

    public toScreen(screen: string) {
        switch (screen) {
            case MessagesSettings.SCREEN_MESSAGE:
            case MessagesSettings.SCREEN_PROPERTIES:
            case MessagesSettings.SCREEN_SAVING:
            case MessagesSettings.SCREEN_SAVE_FAILED:
            case MessagesSettings.SCREEN_SAVE_SUCCESS:
                this.currentScreen = screen;
                break;
            default:
                console.error('Screen: ' + screen + ' does not exist or is not implemented.');
                break;
        }
    }

    public normalizeTag(tag: string) {
        let regex = /_/gi;
        return tag.replace(regex, " ");
    }

    public choosenCategory(tag: string) {
        // store previous tag
        let previousTag = [this.messageCollection.metadata.tags[0]];

        // set the choosen tag as metadata
        this.messageCollection.metadata.tags = [tag];
        // check if a follow-up for this new tag is allowed
        this.settings.followUpAllowed = (this.followUpAllowedTags.indexOf(tag) !== -1);

        // If follow-up(s) exist but a follow up is not allowed
        // Ask the user if he is sure he wants to change the tag
        if (this.settings.followUpAllowed === false && this.messageCollection.messages.length > 1) {
            this.modal.confirm(
                'Change category',
                `Are you sure you want to change category now?
                <br>
                This will remove all your follow-up messages!`
            ).then(
                () => {
                    // The user agreed to changing the tag
                    // remove only the follow ups
                    for (let i = this.messageCollection.messages.length; i > 0; i--) {
                        this.delMessage(new Event('API'), i);
                    }
                },
                () => {
                    // The user declined to changing the tag
                    // restore data
                    // this works because the only time this code is reached
                    // is when the previousTag was a follow-up allowed tag
                    this.messageCollection.metadata.tags = previousTag;
                    this.settings.followUpAllowed = true;
                }
            )
        }
    }

    protected determineMessageSettings() {
        let messages  = this.messageCollection.messages.length;
        this.allValid = this.messageCollection.languageId !== 0;

        var hasGenericAttachment = null;

        this.messageCollection.messages.forEach((message: Message, index) => {
            if (message.attachmentType === Attachment.TYPE_GENERIC) {
                hasGenericAttachment = index;
            }
        });

        this.messageFlags.forEach((data, index) => {
            if (data.isValid === false) {
                this.allValid = false;
            }

            data.settings.allowGenericAttachment = (hasGenericAttachment === null || hasGenericAttachment === index);
        });

        if (this.allValid === false) {
            return;
        }

        // There should be at least one message
        this.allValid = messages >= this.settings.minMessages;
        if (this.allValid === false) {
            return;
        }

        // Tags are required
        this.allValid = this.messageCollection.metadata.tags.length > 0;
        if (this.allValid === false) {
            return;
        }

        this.allValid = (messages > 1 || this.messageCollection.messages[0].type === Message.TYPE_NORMAL);
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/messages/components/messages.component.ts